import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { decryptData } from '../services/crypto';
import { useGetProfileCardDataQuery } from '../features/profileSlice';
import { Loader } from '../Shared/Loader';
import { isTokenExpired } from '../services/tokenUtils';
import { store } from '../app/store';
import { apiSlice } from '../features/api/apiSlice';

export const RequireAuth = ({ allowedRoles }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [waitingAfterLogin, setWaitingAfterLogin] = useState(false);

  let staffId = null;
  const encryptedId = localStorage.getItem("id");
  if (encryptedId) {
    try {
      staffId = decryptData(encryptedId);
    } catch (error) {
      console.error("Failed to decrypt id:", error);
    }
  }

  const { isLoading, error, refetch } = useGetProfileCardDataQuery({ employeeId: staffId });

  // handle fresh login (justLoggedIn)
  // If we detect a fresh login, wait a short time then refetch profile
  useEffect(() => {
    const justLogged = sessionStorage.getItem("justLoggedIn");
    if (justLogged) {
      setWaitingAfterLogin(true);
      const t = setTimeout(() => {
        sessionStorage.removeItem("justLoggedIn");
        setWaitingAfterLogin(false);
    if (staffId) {
      refetch();
    }
      }, 500); // Give enough time for localStorage + RTK to resync

      return () => clearTimeout(t);
    }
  }, [staffId, refetch]);


  // load and decrypt role + token
  useEffect(() => {
    const token = localStorage.getItem("token");
    const encryptedRole = localStorage.getItem("role");

    if (!token) {
      setIsReady(true);
      return;
    }

    try {
      const decrypted = decryptData(encryptedRole);
      setUserRole(decrypted);
    } catch {
      setUserRole(null);
    } finally {
      setTimeout(() => setIsReady(true), 150);
    }
  }, []);

  // handle expired token (auto logout)
  useEffect(() => {
    if (error?.data?.error?.statusCode === 401 && isTokenExpired()) {
      const currentPath = location.pathname + location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      sessionStorage.setItem("autoLogout", "true");
      localStorage.clear();
      store.dispatch(apiSlice.util.resetApiState());
      setIsAuthenticated(false);
    }
  }, [error, location]);

  // show loader while not ready
  if (isLoading || !isReady || waitingAfterLogin) {
    return <Loader />;
  }

  // if user is not authenticated
  if (!isAuthenticated || !localStorage.getItem("token")) {
    const redirectPath = sessionStorage.getItem("redirectPath");
    const autoLogout = sessionStorage.getItem("autoLogout");

    const redirectTo = autoLogout && redirectPath ? redirectPath : "/login";
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // role-based access
  const normalizedRole = userRole?.toLowerCase();
  const allowed = (allowedRoles || []).map(r => r?.toLowerCase());
  const canAccess = allowed.includes(normalizedRole);

  if (canAccess) return <Outlet />;

  return normalizedRole
    ? <Navigate to="/unauthorized" state={{ from: location }} replace />
    : <Navigate to="/login" state={{ from: location }} replace />;
};


